import {
  RESULT_VARIANT_CONFIG,
  type GoalOptionDefinition,
} from "@/src/components/OnboardingWizard/quizStepsConfig";
import {
  formatResultCompactCurrency,
  formatResultCurrency,
  formatResultSignedCurrency,
} from "@/src/components/OnboardingWizard/result/formatResultCurrency";
import {
  formatHomeBudgetExtensionLong,
  formatLongTermGainPayoffTitle,
  formatMonthsLongerLong,
  formatYearsSoonerLong,
  resolveDebtPayoffTitle,
} from "@/src/components/OnboardingWizard/result/formatDeltaLabel";
import { mapResultChartPayload } from "@/src/components/OnboardingWizard/result/mapResultChartPayload";
import type { ResultChartPayload } from "@/src/components/OnboardingWizard/result/types";
import type { GoalResultVariant } from "@/src/components/OnboardingWizard/types";
import type {
  OnboardingV3GoalType,
  OnboardingV3ResultValues,
} from "./onboardingV3Types";

export interface ResultUIData {
  variant: GoalResultVariant;
  bubbleLabel: string;
  bubbleQuestion: string;
  heroLine: string;
  heroValue: string;
  heroSubline: string;
  payoffTitle: string;
  payoffBody: string;
  cta: string;
  hasData: boolean;
  chart?: ResultChartPayload;
}

const EMPTY_LABEL = "No data yet";

const readChartRecord = (
  values: Record<string, unknown>,
): Record<string, unknown> | null => {
  const chart = values.chart;
  if (!chart || typeof chart !== "object" || Array.isArray(chart)) {
    return null;
  }
  return chart as Record<string, unknown>;
};

const formatRunwayMonths = (months: number): string => {
  const unit = months === 1 ? "month" : "months";
  return `${months} ${unit}`;
};

const goalTypeToVariant = (
  goalType: OnboardingV3GoalType,
): GoalResultVariant => {
  const map: Record<OnboardingV3GoalType, GoalResultVariant> = {
    million: "million",
    retirement: "retirement",
    home: "home",
    emergency: "runway",
    idlecash: "idle-cash",
    debt: "debt-free",
  };
  return map[goalType];
};

const shellForVariant = (variant: GoalResultVariant) =>
  RESULT_VARIANT_CONFIG[variant];

const baseFromShell = (
  variant: GoalResultVariant,
  overrides: Partial<ResultUIData>,
): ResultUIData => {
  const shell = shellForVariant(variant);
  return {
    variant,
    bubbleLabel: shell.bubbleLabel,
    bubbleQuestion: shell.bubbleQuestion,
    heroLine: shell.heroLine,
    heroValue: shell.heroValue,
    heroSubline: shell.heroSubline,
    payoffTitle: shell.payoffTitle,
    payoffBody: shell.payoffBody,
    cta: shell.cta,
    hasData: false,
    ...overrides,
  };
};

export const mapResultValuesToResultUI = (
  resultValues: OnboardingV3ResultValues | null | undefined,
): ResultUIData => {
  if (!resultValues?.values) {
    return baseFromShell("million", {
      heroValue: EMPTY_LABEL,
      heroSubline: "",
    });
  }

  const { goalType, values } = resultValues;
  const variant = goalTypeToVariant(goalType);
  const shell = shellForVariant(variant);
  const chart = mapResultChartPayload(goalType, values);

  switch (goalType) {
    case "million": {
      const year = values.onYourOwnYear;
      const age = values.onYourOwnAge;
      const targetAmount = values.targetAmount;
      const delta = values.deltaYears;
      return baseFromShell(variant, {
        heroValue: typeof year === "number" ? String(year) : EMPTY_LABEL,
        heroSubline:
          typeof age === "number" && typeof targetAmount === "number"
            ? `that's ${formatResultCurrency(targetAmount)} — around age ${age}.`
            : typeof age === "number"
              ? `that's $1,000,000 — around age ${age}.`
              : shell.heroSubline,
        payoffTitle:
          typeof delta === "number"
            ? formatYearsSoonerLong(delta)
            : shell.payoffTitle,
        hasData: typeof year === "number",
        chart,
      });
    }
    case "retirement": {
      const age = values.onYourOwnAge;
      const yearsFromNow = values.yearsFromNow;
      const delta = values.deltaYears;
      return baseFromShell(variant, {
        heroValue: typeof age === "number" ? String(age) : EMPTY_LABEL,
        heroSubline:
          typeof yearsFromNow === "number"
            ? `that's work-optional — about ${yearsFromNow} years from now.`
            : shell.heroSubline,
        payoffTitle:
          typeof delta === "number"
            ? formatYearsSoonerLong(delta)
            : shell.payoffTitle,
        payoffBody: shell.payoffBody,
        hasData: typeof age === "number",
        chart,
      });
    }
    case "emergency": {
      const months = values.runwayMonths;
      const cashAmount = values.cashAmount;
      const depletionLabel = values.runwayDepletionLabel;
      const delta = values.deltaMonths;
      return baseFromShell(variant, {
        heroValue:
          typeof months === "number" ? formatRunwayMonths(months) : EMPTY_LABEL,
        heroSubline:
          typeof cashAmount === "number"
            ? `that's about ${formatResultCurrency(cashAmount)} in the bank — gone by ${typeof depletionLabel === "string" ? depletionLabel : "then"}.`
            : shell.heroSubline,
        payoffTitle:
          typeof delta === "number"
            ? formatMonthsLongerLong(delta)
            : shell.payoffTitle,
        payoffBody: shell.payoffBody,
        hasData: typeof months === "number",
        chart,
      });
    }
    case "idlecash": {
      const annual = values.annualExtraYield;
      const gain = values.longTermGain;
      const years = values.longTermYears;
      const chartValues = readChartRecord(values);
      const withEdYield =
        chartValues && typeof chartValues.withEdYield === "number"
          ? chartValues.withEdYield
          : null;
      return baseFromShell(variant, {
        heroValue:
          typeof annual === "number"
            ? `${formatResultSignedCurrency(annual)}/yr`
            : EMPTY_LABEL,
        heroSubline:
          typeof withEdYield === "number"
            ? `to start — and left to compound at ~${withEdYield}%, it grows far bigger.`
            : shell.heroSubline,
        payoffTitle:
          typeof gain === "number" && typeof years === "number"
            ? formatLongTermGainPayoffTitle(
                formatResultCompactCurrency(gain),
                years,
              )
            : shell.payoffTitle,
        payoffBody:
          typeof annual === "number"
            ? `That ~${formatResultCurrency(annual)} a year quietly compounds while you sleep.`
            : shell.payoffBody,
        hasData: typeof annual === "number",
        chart,
      });
    }
    case "home": {
      const price = values.onYourOwnHomePrice;
      const delta = values.deltaHomePrice;
      const chartValues = readChartRecord(values);
      const deposit =
        chartValues && typeof chartValues.savedDownPayment === "number"
          ? chartValues.savedDownPayment
          : null;
      return baseFromShell(variant, {
        heroValue:
          typeof price === "number"
            ? formatResultCompactCurrency(price)
            : EMPTY_LABEL,
        heroSubline:
          typeof deposit === "number"
            ? `with your ~${formatResultCurrency(deposit)} deposit and current income.`
            : shell.heroSubline,
        payoffTitle:
          typeof delta === "number"
            ? formatHomeBudgetExtensionLong(formatResultCompactCurrency(delta))
            : shell.payoffTitle,
        payoffBody: shell.payoffBody,
        hasData: typeof price === "number",
        chart,
      });
    }
    case "debt": {
      const year = values.onYourOwnYear;
      const withEdYear = values.withEdYear;
      const yearsOut = values.yearsUntilClear;
      const deltaMonths = values.deltaMonths;
      const currentYear = new Date().getFullYear();
      const resolvedYearsOut =
        typeof yearsOut === "number"
          ? yearsOut
          : typeof year === "number"
            ? Math.max(year - currentYear, 0)
            : null;
      const deltaYears =
        typeof values.deltaYears === "number"
          ? values.deltaYears
          : typeof year === "number" && typeof withEdYear === "number"
            ? Math.max(0, year - withEdYear)
            : null;
      return baseFromShell(variant, {
        heroValue: typeof year === "number" ? String(year) : EMPTY_LABEL,
        heroSubline:
          typeof resolvedYearsOut === "number" && resolvedYearsOut > 0
            ? `that's your last payment — about ${resolvedYearsOut} years out.`
            : shell.heroSubline,
        payoffTitle: resolveDebtPayoffTitle(
          deltaYears,
          typeof deltaMonths === "number" ? deltaMonths : null,
          shell.payoffTitle,
        ),
        payoffBody: shell.payoffBody,
        hasData: typeof year === "number",
        chart,
      });
    }
    default:
      return baseFromShell(variant, {
        heroValue: EMPTY_LABEL,
      });
  }
};

export const resolveResultVariantFromGoalOption = (
  option: GoalOptionDefinition,
): GoalResultVariant => option.resultVariant;
